<template>
  <div class="home">
    <div class="main" v-if="showResult">
      <Result
        :icon="icon"
        :title="resultTitle"
        :btn-text="resultButtonText"
        @dismissFunc="dismiss"
      />
    </div>
    <div v-else class="d-flex flex-column">
      <IdentityVerification
        v-if="validationResponse"
        @next="($event) => sendValidationResult($event)"
        @setLoading="(isLoading) => setLoading(isLoading)"
        @onValidationFailed="sendValidationFailed"
        :require-validation-response="validationResponse"
        :hide-close-button="true"
      />
    </div>
  </div>
  <div v-if="loading" class="loading-overlay">
    <i class="fa fa-spin fa-spinner" />
  </div>
</template>

<script lang="ts">
import {
  computed,
  defineComponent,
  getCurrentInstance,
  onMounted,
  ref,
} from "vue";
import { useRouter, useRoute } from "vue-router";
import { setLanguage } from "@/apps/credit-line/helpers/translations.helper";
import { AndroidInterface } from "@/apps/credit-line/services/android.service";
import { ErrorHandler } from "@/apps/credit-line/helpers/errorHandlerWithLogger.helper";
import { AuthenticationHelper } from "@/apps/credit-line/helpers/authentication.helper";
import logAnalyticsEvent from "@/apps/credit-line/services/google-analytics-events.service";
import AnalyticEvents from "@/shared-helpers/google-analytics-events.helper";
import { FormatBankAccountHelper } from "@/apps/credit-line/helpers/formatBankAccount.helper";
import { recorder, RECORDING_APPS } from "@/shared-services/recording.service";
import useAsset from "@/apps/credit-line/helpers/useAsset";
import Result from "@/apps/credit-line/components/Result.vue";
import { useI18n } from "@/apps/credit-line/composables/useI18n";
import { AreValidationsRequiredResponse } from "@/apps/credit-line/models/IKycValidations";
import { getRandomNumberBetweenRange } from "@/apps/credit-line/helpers/misc.helper";
import { useCreditLineSummary } from "@/apps/credit-line/hooks/useCreditLineSummary";
import { isAndroid } from "@/apps/credit-line/helpers/platformChecker.helper";
import IdentityVerification from "@/apps/credit-line/components/Withdrawal/IdentityVerification.vue";
import { areValidationRequired as areValidationRequiredService } from "../../services/kyc";
import { KycUserFlow } from "../../enums/kycEnums";
import { AxiosError } from "axios";

declare const Android: AndroidInterface;

export default defineComponent({
  name: "CreditLineWithdrawDefrostView",
  computed: {
    KycUserFlow() {
      return KycUserFlow;
    },
  },
  methods: {
    FormatBankAccountHelper,
    getIcon(path: string): string {
      return useAsset(path);
    },
  },
  components: {
    //CurpFourLastCharactersDefrostValidation,
    Result,
    IdentityVerification,
  },
  beforeMount() {
    setLanguage(this);
  },
  setup() {
    const { t } = useI18n();
    const router = useRouter();
    const route = useRoute();
    const navbarType = ref<string>("goBack");
    const screenTitle = ref<string>("");
    const creditLineId = ref<number>(0);
    const loading = ref<boolean>(false);
    const requiresIdentityVerification = ref<boolean>(true);
    const requiresIdentityValidationId = ref(0);
    const validationResponse = ref<AreValidationsRequiredResponse | null>(null);
    const app = getCurrentInstance()?.appContext.app;
    const userId = ref<string>("");
    const showResult = ref<boolean>(false);
    const resultTitle = ref<string>("");
    const resultDescription = ref<string>("");
    const resultDescription2 = ref<string>("");
    const resultButtonText = ref<string>("");
    const selectWithdrawCardTitle = ref<string>("");
    const selectWithdrawCardMessage = ref<string>("");
    const openRequestCardBanner = ref<boolean>(false);
    const cardBannerDismissable = ref<boolean>(true);
    const bankAccountErrorMessage = ref("");
    const newUI = computed(
      () => typeof Android.shouldDisplayNewUi() === "boolean"
    );
    const { fetchSummary, summary } = useCreditLineSummary();
    const showIdentityValidation = ref<boolean>(false);
    const icon = ref<string>("");
    const validationSuccess = ref<boolean>(false);
    const isShowCloseContainerOn = ref<boolean>(true);

    const areValidationsRequired = async () => {
      try {
        const { data } = await areValidationRequiredService({
          creditLineId: creditLineId.value,
          userFlow: KycUserFlow.creditLineDefrostWithdrawal,
          deviceTag: AuthenticationHelper.getDeviceTag(),
        });
        if (data.wasSuccessful && data.requiredValidations.length > 0) {
          validationResponse.value = data;
          requiresIdentityValidationId.value = data.requiredValidations[0].id;
          showIdentityValidation.value = true;
        }
      } catch (err) {
        ErrorHandler.handleNetworkErrorAndLog(err as AxiosError);
      }
    };

    const goBack = () => {
      Android.dismissWebView();
    };

    const sendValidationFailed = () => {
      validationSuccess.value = false;
      icon.value = useAsset("img/credit-line/error_warning_icon.svg");
      resultTitle.value = t("CL_WITHDRAWAL.CURP_VALIDATION_ERROR_1");
      resultButtonText.value = t("CLOSE");
      loading.value = false;
      showResult.value = true;
      logAnalyticsEvent(
        AnalyticEvents.CreditLine.ClickDefrostWithdraw,
        "Happy Path",
        "Withdrawal defrost Flow finish failing"
      );
    };

    const hideCloseContainer = () => {
      isShowCloseContainerOn.value = false;
    };

    const showCloseContainer = () => {
      isShowCloseContainerOn.value = true;
    };

    onMounted(() => {
      logAnalyticsEvent(
        AnalyticEvents.CreditLine.ClickDefrostWithdraw,
        "Happy Path",
        "Withdrawal defrost Flow started"
      );
      recorder(app)?.startRecording(RECORDING_APPS.CREDIT_LINE);
      recorder(app)?.updateRecordingInfo(
        getRandomNumberBetweenRange(1000000, 1),
        {
          deviceTag: AuthenticationHelper.getDeviceTag(),
        }
      );
      fetchSummary()
        .then((summary) => {
          creditLineId.value = summary.creditLineId;
          areValidationsRequired();
        })
        .finally(() => {
          if (isAndroid()) {
            Android.finishedLoading();
          }
        });
    });

    const setScreenTitle = (title: string) => (screenTitle.value = title);

    const setLoading = (isLoading: boolean) => (loading.value = isLoading);

    const sendValidationResult = (result) => {
      if (result) {
        //send defrost confirmation
        icon.value = useAsset("img/credit-line/cc_checkmark.svg");
        validationSuccess.value = true;
        resultTitle.value = t("CL_DEFROST_WITHDRAWAL.RESULT_TITLE");
        resultDescription.value = "Now you can use your founds normally";
        resultButtonText.value = t("CL_DEFROST_WITHDRAWAL.RESULT_NEXT");
        loading.value = false;
        showResult.value = true;
        logAnalyticsEvent(
          AnalyticEvents.CreditLine.ClickDefrostWithdraw,
          "Happy Path",
          "Withdrawal defrost Flow finish successfully"
        );
      }
    };

    const setShowResult = (newVal: boolean) => {
      showResult.value = newVal;
    };

    const dismiss = () => {
      if (
        route.query.isServerMessage &&
        route.query.isServerMessage == "true"
      ) {
        Android.dismissWebView();
      } else {
        router.push({
          name: "CreditLineWithdrawView",
          query: { reload: "true" },
        });
      }
    };

    return {
      t,
      router,
      newUI,
      loading,
      navbarType,
      screenTitle,
      creditLineId,
      openRequestCardBanner,
      cardBannerDismissable,
      requiresIdentityVerification,
      userId,
      showResult,
      resultTitle,
      resultDescription,
      resultDescription2,
      resultButtonText,
      setScreenTitle,
      requiresIdentityValidationId,
      setLoading,
      setShowResult,
      selectWithdrawCardTitle,
      selectWithdrawCardMessage,
      dismiss,
      bankAccountErrorMessage,
      goBack,
      sendValidationFailed,
      sendValidationResult,
      hideCloseContainer,
      showCloseContainer,
      showIdentityValidation,
      icon,
      isShowCloseContainerOn,
      summary,
      validationResponse,
    };
  },
});
</script>
<style lang="scss" scoped>
@import "@/assets/scss/constants";

.upper-close {
  font-size: 1.5em;
}

.header-container {
  padding-top: 0px;
  width: 100%;
  position: fixed;
  left: 0;
  top: 0;
  z-index: 200;
}

.header-container {
  padding-top: 0px;
  width: 100%;
  position: fixed;
  left: 0;
  top: 0;
  z-index: 200;
}

.loading-overlay {
  position: fixed;
  top: 0;
  left: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  color: $pj-color-green;
  font-size: 40px;
  background-color: rgba(150, 150, 150, 0.4);
  width: 100vw;
  height: 100vh;
  z-index: 1000;
}

.link {
  color: $pj-color-green;
  font-weight: 600;
  clear: both;
  text-decoration: none;
  font-size: 16px;
}
</style>
